import type { LineElement } from "@ai-teach/types";

interface LineRendererProps {
  element: Partial<LineElement>;
}

export function LineRenderer({ element }: LineRendererProps) {
  const {
    startX = 0,
    startY = 0,
    endX = 100,
    endY = 100,
    stroke = "#000000",
    strokeWidth = 2,
    lineStyle = "solid",
    startArrow = "none",
    endArrow = "none",
    opacity = 1,
    dashArray,
  } = element;

  // Calculate line properties
  const dx = endX - startX;
  const dy = endY - startY;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx);

  // Get dash array based on line style
  const getDashArray = () => {
    if (dashArray) return dashArray;

    switch (lineStyle) {
      case "dashed":
        return "10,5";
      case "dotted":
        return "2,3";
      case "wavy":
        return "none";
      default:
        return "none";
    }
  };

  // Generate wavy path
  const getWavyPath = () => {
    const waves = Math.floor(length / 20);
    const amplitude = 5;

    let path = `M ${startX} ${startY}`;

    for (let i = 1; i <= waves; i++) {
      const t = i / waves;
      const x = startX + dx * t;
      const y = startY + dy * t;
      const offsetX = -Math.sin(angle) * amplitude * Math.sin(i * Math.PI);
      const offsetY = Math.cos(angle) * amplitude * Math.sin(i * Math.PI);
      path += ` Q ${x + offsetX} ${y + offsetY} ${x} ${y}`;
    }

    path += ` L ${endX} ${endY}`;
    return path;
  };

  // Arrow marker paths
  const getArrowPath = (type: string, id: string, position: "start" | "end") => {
    const markerSize = 6 + strokeWidth * 1.5;
    const refX = markerSize / 3;
    const refY = markerSize / 2;

    switch (type) {
      case "arrow":
        return (
          <marker
            id={id}
            markerWidth={markerSize}
            markerHeight={markerSize}
            refX={refX}
            refY={refY}
            orient={position === "start" ? "auto-start-reverse" : "auto"}
            markerUnits="userSpaceOnUse"
          >
            <path d={`M0,0 L0,${markerSize} L${markerSize},${refY} z`} fill={stroke} />
          </marker>
        );

      case "circle":
        return (
          <marker
            id={id}
            markerWidth={markerSize}
            markerHeight={markerSize}
            refX={refX}
            refY={refY}
            orient={position === "start" ? "auto-start-reverse" : "auto"}
            markerUnits="userSpaceOnUse"
          >
            <circle cx={refY} cy={refY} r={markerSize / 3} fill={stroke} />
          </marker>
        );

      case "square":
        return (
          <marker
            id={id}
            markerWidth={markerSize}
            markerHeight={markerSize}
            refX={refX}
            refY={refY}
            orient={position === "start" ? "auto-start-reverse" : "auto"}
            markerUnits="userSpaceOnUse"
          >
            <rect
              x={markerSize / 6}
              y={markerSize / 6}
              width={(markerSize * 2) / 3}
              height={(markerSize * 2) / 3}
              fill={stroke}
            />
          </marker>
        );

      case "diamond":
        return (
          <marker
            id={id}
            markerWidth={markerSize}
            markerHeight={markerSize}
            refX={refX}
            refY={refY}
            orient={position === "start" ? "auto-start-reverse" : "auto"}
            markerUnits="userSpaceOnUse"
          >
            <path
              d={`M${refY},${markerSize / 6} L${(markerSize * 5) / 6},${refY} L${refY},${(markerSize * 5) / 6} L${markerSize / 6},${refY} z`}
              fill={stroke}
            />
          </marker>
        );

      default:
        return null;
    }
  };

  const startMarkerId = `start-${element.id || "marker"}`;
  const endMarkerId = `end-${element.id || "marker"}`;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        overflow: "visible",
      }}
    >
      <title>Line SVG</title>
      <defs>
        {startArrow !== "none" && getArrowPath(startArrow, startMarkerId, "start")}
        {endArrow !== "none" && getArrowPath(endArrow, endMarkerId, "end")}
      </defs>

      {lineStyle === "wavy" ? (
        <path
          d={getWavyPath()}
          stroke={stroke}
          strokeWidth={strokeWidth}
          fill="none"
          opacity={opacity}
          markerStart={startArrow !== "none" ? `url(#${startMarkerId})` : undefined}
          markerEnd={endArrow !== "none" ? `url(#${endMarkerId})` : undefined}
        />
      ) : (
        <line
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={getDashArray()}
          opacity={opacity}
          markerStart={startArrow !== "none" ? `url(#${startMarkerId})` : undefined}
          markerEnd={endArrow !== "none" ? `url(#${endMarkerId})` : undefined}
        />
      )}
    </svg>
  );
}
